import { GitCompare, ChevronRight, Activity, Scale, Info } from "lucide-react";

const C = {
  surface: "var(--bc-panel, #18181B)", highlight: "var(--bc-panel-elev, #27272A)", border: "var(--bc-border, #27272A)",
  text: "var(--bc-ink, #FAFAFA)", muted: "var(--bc-sub, #A1A1AA)", green: "#22C55E",
  red: "#EF4444", blue: "#3B82F6", yellow: "#F59E0B", cyan: "#06B6D4",
  purple: "#A78BFA",
};

// ─── Adaptive-weights explainer (tier + phase → P/E/F split) ─────────────────
const TIER_LABEL = {
  A: "Top-tier", B: "Secondario", C: "Lega minore", CUP: "Coppa",
};
const TIER_COLOR = {
  A: "#22C55E", B: "#06B6D4", C: "#F59E0B", CUP: "#A78BFA",
};
const PHASE_LABEL = {
  early: "inizio stagione", mid: "metà stagione", late: "fine stagione",
};

function EnsembleWeightsBar({ label, value, color, dominant }) {
  return (
    <div className="flex items-center gap-3">
      <span
        className="font-heading font-bold text-[10px] uppercase tracking-widest flex-shrink-0"
        style={{ color: dominant ? color : C.muted, width: 72 }}
      >
        {label}
      </span>
      <div
        className="flex-1 h-1.5 relative overflow-hidden"
        style={{ background: C.highlight }}
      >
        <div
          className="absolute top-0 left-0 h-full transition-all"
          style={{
            width: `${value}%`,
            background: color,
            boxShadow: dominant ? `0 0 8px ${color}80` : "none",
          }}
        />
        {dominant && (
          <div
            className="absolute top-0 h-full w-[2px]"
            style={{
              left: `${value}%`,
              background: "rgba(255,255,255,0.9)",
              boxShadow: "0 0 6px rgba(255,255,255,0.8)",
              transform: "translateX(-1px)",
            }}
          />
        )}
      </div>
      <span
        className="font-data font-bold text-xs tabular-nums flex-shrink-0"
        style={{ color: dominant ? color : C.muted, width: 44, textAlign: "right" }}
      >
        {value}%
      </span>
    </div>
  );
}

function EnsembleWeightsWidget({ weights }) {
  if (!weights || weights.poisson == null) return null;
  const p = Math.round(weights.poisson * 100);
  const e = Math.round(weights.elo * 100);
  const f = Math.round(weights.form * 100);
  const maxVal = Math.max(p, e, f);
  const tier = weights.tier || "?";
  const phase = weights.phase || "?";
  const tierColor = TIER_COLOR[tier] || C.muted;
  return (
    <div
      data-testid="ensemble-weights-widget"
      className="p-4"
      style={{
        background: C.surface,
        border: `1px solid ${C.border}`,
        borderLeft: `2px solid ${tierColor}`,
      }}
    >
      <div className="flex items-center gap-2 mb-3 flex-wrap">
        <Scale size={13} style={{ color: tierColor }} />
        <span
          className="font-heading font-black text-xs uppercase tracking-[0.2em]"
          style={{ color: C.text }}
        >
          MIX MODELLI
        </span>
        <span
          className="font-data text-[10px] px-2 py-0.5 uppercase tracking-widest"
          style={{
            color: tierColor,
            background: `${tierColor}12`,
            border: `1px solid ${tierColor}35`,
          }}
        >
          {TIER_LABEL[tier] || tier}
        </span>
        <span
          className="font-data text-[10px] uppercase tracking-wider"
          style={{ color: C.muted }}
        >
          · {PHASE_LABEL[phase] || phase}
        </span>
      </div>
      <div className="space-y-2">
        <EnsembleWeightsBar label="Poisson" value={p} color={C.cyan} dominant={p === maxVal} />
        <EnsembleWeightsBar label="Elo" value={e} color={C.yellow} dominant={e === maxVal} />
        <EnsembleWeightsBar label="Forma" value={f} color={C.green} dominant={f === maxVal} />
      </div>
      {weights.reason && (
        <div
          className="mt-3 pt-3 flex items-start gap-2"
          style={{ borderTop: `1px solid ${C.highlight}` }}
        >
          <Info size={11} style={{ color: C.muted, flexShrink: 0, marginTop: 1 }} />
          <span
            className="font-data text-[10px] leading-relaxed"
            style={{ color: C.muted }}
          >
            {weights.reason}
          </span>
        </div>
      )}
    </div>
  );
}

function AlgoRow({ model, probs, isCombined, color }) {
  const rowColor = color || (isCombined ? C.green : C.muted);
  const maxVal = Math.max(probs.home, probs.draw, probs.away);
  return (
    <tr style={{ borderBottom: `1px solid ${C.highlight}`, background: isCombined ? "rgba(34,197,94,0.04)" : "transparent" }}>
      <td className="py-3 px-4">
        <div className="flex items-center gap-2">
          {isCombined && <div className="w-1.5 h-1.5" style={{ background: rowColor }} />}
          <span className="font-heading font-bold text-xs uppercase tracking-wider"
            style={{ color: rowColor }}>{model}</span>
        </div>
      </td>
      {[
        { val: probs.home, baseColor: C.green },
        { val: probs.draw, baseColor: C.muted },
        { val: probs.away, baseColor: C.red },
      ].map(({ val, baseColor }, i) => (
        <td key={i} className="py-3 px-3 text-center">
          <span className="font-data font-medium text-sm"
            style={{ color: val === maxVal ? baseColor : C.muted }}>
            {val}%
          </span>
          {val === maxVal && <div className="w-4 h-0.5 mx-auto mt-1" style={{ background: baseColor }} />}
        </td>
      ))}
    </tr>
  );
}

function ConvergenceBar({ score, models, avgSpread, isNoBet, reason }) {
  const barColor = isNoBet ? C.red : score >= 75 ? C.green : score >= 55 ? C.yellow : C.red;
  const label = isNoBet ? "NO BET" : score >= 75 ? "ALTA" : score >= 55 ? "MEDIA" : "BASSA";
  const modelIcons = { home: "1", draw: "X", away: "2" };
  const MODEL_LABELS = { poisson: "Poisson", elo: "ELO", form: "Forma" };

  return (
    <div style={{ background: `${barColor}08`, border: `1px solid ${barColor}25`, borderTop: `2px solid ${barColor}` }}>
      <div className="px-4 py-3 flex items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <Activity size={12} style={{ color: barColor }} />
          <span className="font-heading font-bold text-xs uppercase tracking-widest" style={{ color: barColor }}>
            CONVERGENZA MODELLI
          </span>
        </div>
        <div className="flex items-center gap-2">
          <span className="font-data text-xl font-bold" style={{ color: barColor }}>{score}</span>
          <span className="font-data text-xs" style={{ color: C.muted }}>/100</span>
          <span className="font-data text-xs px-2 py-0.5 uppercase tracking-wider"
            style={{ background: `${barColor}18`, color: barColor, border: `1px solid ${barColor}30` }}>
            {label}
          </span>
        </div>
      </div>

      {/* Progress bar — Neon Glow */}
      <div className="px-4 pb-3">
        <div
          className="h-2.5 w-full overflow-hidden relative"
          style={{ background: "var(--bc-panel-elev, var(--bc-panel-elev, rgba(255,255,255,0.03)))", borderRadius: 4, border: "1px solid var(--bc-border, rgba(255,255,255,0.07))" }}
        >
          <div
            className="h-full transition-all duration-700"
            style={{
              width: `${score}%`,
              background: `linear-gradient(to right, ${barColor}40, ${barColor}90, ${barColor})`,
              boxShadow: `0 0 14px ${barColor}70, 0 0 28px ${barColor}30`,
              borderRadius: "3px 0 0 3px",
              position: "relative",
              overflow: "hidden",
            }}
          >
            {/* shimmer */}
            <div style={{
              position: "absolute", top: 0, left: 0, right: 0, height: "45%",
              background: "linear-gradient(to bottom, rgba(255,255,255,0.25), transparent)",
              borderRadius: "3px 0 0 0",
            }} />
          </div>
        </div>
      </div>

      {/* Per-model outcome */}
      {models && (
        <div className="grid grid-cols-3 px-4 pb-3 gap-2">
          {Object.entries(MODEL_LABELS).map(([key, label]) => {
            const outcome = models[key];
            const outcomeColor = outcome === "home" ? C.green : outcome === "away" ? C.red : C.muted;
            return (
              <div key={key} className="flex flex-col items-center gap-0.5">
                <span className="font-data text-[10px] uppercase" style={{ color: C.muted }}>{label}</span>
                <span className="font-data text-sm font-bold px-2 py-0.5"
                  style={{ color: outcomeColor, background: `${outcomeColor}12`, border: `1px solid ${outcomeColor}25` }}>
                  {outcome ? modelIcons[outcome] : "?"}
                </span>
              </div>
            );
          })}
        </div>
      )}

      {/* Reason if NO BET */}
      {isNoBet && reason && (
        <div className="px-4 pb-3">
          <p className="font-data text-xs" style={{ color: C.muted }}>{reason}</p>
        </div>
      )}

      {/* Spread note */}
      {!isNoBet && avgSpread !== undefined && (
        <div className="px-4 pb-3">
          <p className="font-data text-xs" style={{ color: C.muted }}>
            Spread medio tra modelli: <span style={{ color: C.text }}>{avgSpread}%</span>
          </p>
        </div>
      )}
    </div>
  );
}

export default function AlgorithmPanel({ predictions, homeTeam, awayTeam }) {
  const { poisson, elo, combined, form_probs, convergence, ensemble_weights } = predictions;

  return (
    <div className="space-y-4">
      {/* Convergence indicator */}
      {convergence && (
        <ConvergenceBar
          score={convergence.score}
          models={convergence.models}
          avgSpread={convergence.avg_spread}
          isNoBet={convergence.is_no_bet}
          reason={convergence.reason}
        />
      )}

      {/* Adaptive ensemble weights */}
      {ensemble_weights && <EnsembleWeightsWidget weights={ensemble_weights} />}

      {/* Algorithm Comparison */}
      <div style={{ background: C.surface, border: `1px solid ${C.border}` }} data-testid="algorithm-panel">
        <div className="flex items-center gap-2 px-4 py-3" style={{ borderBottom: `1px solid ${C.border}` }}>
          <GitCompare size={14} style={{ color: C.blue }} />
          <span className="font-heading font-bold text-sm uppercase tracking-widest" style={{ color: C.text }}>
            CONFRONTO ALGORITMI
          </span>
        </div>

        <table className="w-full" style={{ borderCollapse: "collapse" }}>
          <thead>
            <tr style={{ background: C.highlight }}>
              <th className="font-data text-xs uppercase py-2 px-4 text-left" style={{ color: C.muted }}>MODELLO</th>
              <th className="font-data text-xs uppercase py-2 px-3 text-center w-20" style={{ color: C.green }}>1</th>
              <th className="font-data text-xs uppercase py-2 px-3 text-center w-20" style={{ color: C.muted }}>X</th>
              <th className="font-data text-xs uppercase py-2 px-3 text-center w-20" style={{ color: C.red }}>2</th>
            </tr>
          </thead>
          <tbody>
            <AlgoRow model="Poisson + Dixon-Coles" probs={poisson} />
            <AlgoRow model="ELO Rating" probs={elo} />
            {form_probs && <AlgoRow model="Forma Recente" probs={form_probs} color={C.cyan} />}
            <AlgoRow model="COMBINATO" probs={combined} isCombined />
          </tbody>
        </table>

        {/* ELO details + xG */}
        <div className="px-4 py-2.5 flex flex-wrap gap-x-4 gap-y-1" style={{ borderTop: `1px solid ${C.border}` }}>
          <div className="flex items-center gap-2">
            <span className="font-data text-xs" style={{ color: C.muted }}>ELO CASA:</span>
            <span className="font-data text-xs font-medium" style={{ color: C.green }}>{elo.home_elo}</span>
          </div>
          <div className="flex items-center gap-2">
            <span className="font-data text-xs" style={{ color: C.muted }}>ELO OSPITE:</span>
            <span className="font-data text-xs font-medium" style={{ color: C.red }}>{elo.away_elo}</span>
          </div>
          <div className="flex items-center gap-2">
            <span className="font-data text-xs" style={{ color: C.muted }}>xG casa/ospite:</span>
            <span className="font-data text-xs font-medium" style={{ color: C.text }}>
              {combined.home_xg} / {combined.away_xg}
            </span>
          </div>
        </div>
      </div>

      {/* Algorithm Notes */}
      <div style={{ background: C.surface, border: `1px solid ${C.border}` }}>
        <div className="px-4 py-3" style={{ borderBottom: `1px solid ${C.border}` }}>
          <span className="font-heading font-bold text-sm uppercase tracking-widest" style={{ color: C.text }}>
            METODOLOGIA
          </span>
        </div>
        <div className="px-4 py-3 space-y-2.5">
          {[
            { name: "Poisson + Dixon-Coles", desc: "Distribuzione di Poisson per i gol attesi (xG), con correzione Dixon-Coles per i risultati a basso punteggio. Peso nel combinato: 50%." },
            { name: "ELO Rating", desc: "Sistema ELO adattato al calcio. Rating aggiornato sulle ultime 10 gare con K=32. Include vantaggio casalingo (+65 pts). Peso: 25%." },
            { name: "Forma Recente", desc: "Modello Bradley-Terry sulle ultime 5 gare: forza della squadra (PPG) vs avversario. Considera il vantaggio casalingo (+12%). Peso: 25%." },
            { name: "Convergenza e NO BET", desc: "Se i 3 modelli divergono (spread medio > 18% o esiti discordanti), viene segnalato 'NO BET': nessuna scommessa è consigliata per quella partita." },
          ].map(({ name, desc }) => (
            <div key={name} className="flex gap-2 py-2" style={{ borderBottom: `1px solid ${C.highlight}` }}>
              <ChevronRight size={12} className="flex-shrink-0 mt-0.5" style={{ color: C.green }} />
              <div>
                <p className="font-heading font-bold text-xs uppercase" style={{ color: C.text }}>{name}</p>
                <p className="text-xs mt-0.5" style={{ color: C.muted, fontFamily: "DM Sans, sans-serif", lineHeight: 1.5 }}>{desc}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
